import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Camera, 
  X, 
  CloudArrowUp, 
  Image as ImageIcon,
  ArrowRight,
  Warning,
  Check,
  Car,
  MagnifyingGlass,
  Wrench,
  FileText,
  CaretDown
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AnalysisPage = () => {
  const navigate = useNavigate();
  const [analysisType, setAnalysisType] = useState(null);
  const [images, setImages] = useState([]);
  const [formData, setFormData] = useState({
    vin: '',
    registration: '',
    comment: '',
    damaged_side: ''
  });
  const [gdprConsent, setGdprConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showOptionalFields, setShowOptionalFields] = useState(false);

  const onDrop = useCallback((acceptedFiles) => {
    const maxImages = analysisType === 'part' ? 3 : 6;
    const remainingSlots = maxImages - images.length;
    const newFiles = acceptedFiles.slice(0, remainingSlots);
    
    newFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        setImages(prev => [...prev, {
          file,
          preview: reader.result
        }]);
      };
      reader.readAsDataURL(file);
    });
  }, [images.length, analysisType]);

  const cameraInputRef = React.useRef(null);

  const handleCameraCapture = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const maxImg = analysisType === 'part' ? 3 : 6;
    if (images.length >= maxImg) return;
    const reader = new FileReader();
    reader.onload = () => {
      setImages(prev => [...prev, { file, preview: reader.result }]);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const maxImages = analysisType === 'part' ? 3 : 6;

  const acceptedTypes = analysisType === 'expertise' 
    ? {
        'image/jpeg': ['.jpg', '.jpeg'],
        'image/png': ['.png'],
        'image/webp': ['.webp'],
        'application/pdf': ['.pdf']
      }
    : {
        'image/jpeg': ['.jpg', '.jpeg'],
        'image/png': ['.png'],
        'image/webp': ['.webp']
      };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedTypes,
    maxSize: 10 * 1024 * 1024,
    disabled: images.length >= maxImages
  });

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (images.length === 0) {
      toast.error('Veuillez ajouter au moins une photo');
      return;
    }

    if (!gdprConsent) {
      toast.error('Veuillez accepter les conditions d\'utilisation');
      return;
    }

    setIsSubmitting(true);
    setProgress(10);

    try {
      const analysisData = {
        ...formData,
        images: images.map(img => img.preview),
        gdpr_consent: gdprConsent,
        analysis_type: analysisType
      };

      setProgress(30);
      const createResponse = await axios.post(`${API_URL}/api/analyses`, analysisData);
      const analysisId = createResponse.data.id;

      setProgress(50);
      setIsAnalyzing(true);

      // Fire the analysis (returns immediately, runs in background)
      await axios.post(`${API_URL}/api/analyses/${analysisId}/analyze`);
      
      // Poll for completion (redirect on 'partial' or 'completed')
      let attempts = 0;
      const maxAttempts = 60; // 60 * 3s = 3 min max
      const pollInterval = 3000;
      
      const pollResult = await new Promise((resolve, reject) => {
        const poll = setInterval(async () => {
          attempts++;
          try {
            const statusResp = await axios.get(`${API_URL}/api/analyses/${analysisId}`);
            const status = statusResp.data.status;
            
            // Animate progress between 50% and 95%
            const pollProgress = Math.min(50 + (attempts / maxAttempts) * 45, 95);
            setProgress(Math.round(pollProgress));
            
            if (status === 'partial' || status === 'completed') {
              clearInterval(poll);
              resolve(statusResp.data);
            } else if (status === 'error') {
              clearInterval(poll);
              reject(new Error(statusResp.data.error_message || 'Erreur lors de l\'analyse'));
            }
            
            if (attempts >= maxAttempts) {
              clearInterval(poll);
              reject(new Error('L\'analyse prend trop de temps. Vérifiez vos résultats plus tard.'));
            }
          } catch (err) {
            // Network error during poll, continue trying
            if (attempts >= maxAttempts) {
              clearInterval(poll);
              reject(err);
            }
          }
        }, pollInterval);
      });
      
      setProgress(100);
      toast.success('Analyse terminée !');
      
      setTimeout(() => {
        navigate(`/resultats/${analysisId}`);
      }, 500);

    } catch (error) {
      console.error('Analysis error:', error);
      toast.error(error.message || error.response?.data?.detail || 'Erreur lors de l\'analyse');
      setIsSubmitting(false);
      setIsAnalyzing(false);
      setProgress(0);
    }
  };

  // Selection screen
  if (!analysisType) {
    return (
      <div className="min-h-screen bg-[#F5F5F5]">
        <Header />
        <main className="pt-24 pb-16 px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-12"
            >
              <span className="text-[#E63946] text-xs uppercase tracking-[0.3em] font-bold">
                Que souhaitez-vous faire ?
              </span>
              <h1 className="font-['Outfit'] text-3xl md:text-4xl font-bold text-gray-900 mt-4 mb-4">
                Choisissez votre type d'analyse
              </h1>
              <p className="text-gray-500 max-w-2xl mx-auto">
                Sélectionnez l'option qui correspond à votre besoin
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                onClick={() => setAnalysisType('damage')}
                className="bg-white border border-gray-200 p-8 text-left hover:border-[#E63946]/50 transition-all group shadow-sm"
                data-testid="select-damage-analysis"
              >
                <div className="w-16 h-16 bg-[#E63946]/10 border border-[#E63946]/30 flex items-center justify-center mb-6 group-hover:bg-[#E63946]/20 transition-colors">
                  <Car size={32} className="text-[#E63946]" />
                </div>
                <h3 className="font-['Outfit'] text-xl font-bold text-gray-900 mb-3">
                  Analyser & Chiffrer mon sinistre
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-4">
                  Envoyez jusqu'à 6 photos de votre véhicule accidenté. Notre système analysera 
                  les dommages et vous proposera une liste complète des pièces potentiellement 
                  endommagées avec leurs prix estimés.
                </p>
                <div className="flex items-center gap-2 text-[#E63946] text-sm font-semibold">
                  <span>Commencer l'analyse</span>
                  <ArrowRight size={16} />
                </div>
              </motion.button>

              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                onClick={() => setAnalysisType('part')}
                className="bg-white border border-gray-200 p-8 text-left hover:border-[#2A9D8F]/50 transition-all group shadow-sm"
                data-testid="select-part-search"
              >
                <div className="w-16 h-16 bg-[#2A9D8F]/10 border border-[#2A9D8F]/30 flex items-center justify-center mb-6 group-hover:bg-[#2A9D8F]/20 transition-colors">
                  <MagnifyingGlass size={32} className="text-[#2A9D8F]" />
                </div>
                <h3 className="font-['Outfit'] text-xl font-bold text-gray-900 mb-3">
                  Je cherche une pièce
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-4">
                  Vous avez une pièce à remplacer ? Envoyez simplement une photo de la pièce 
                  et notre système l'identifiera automatiquement avec son prix moyen estimé 
                  sur le marché de l'occasion.
                </p>
                <div className="flex items-center gap-2 text-[#2A9D8F] text-sm font-semibold">
                  <span>Identifier ma pièce</span>
                  <ArrowRight size={16} />
                </div>
              </motion.button>

              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                onClick={() => setAnalysisType('expertise')}
                className="bg-white border border-gray-200 p-8 text-left hover:border-[#8B5CF6]/50 transition-all group shadow-sm"
                data-testid="select-expertise-analysis"
              >
                <div className="w-16 h-16 bg-[#8B5CF6]/10 border border-[#8B5CF6]/30 flex items-center justify-center mb-6 group-hover:bg-[#8B5CF6]/20 transition-colors">
                  <FileText size={32} className="text-[#8B5CF6]" />
                </div>
                <h3 className="font-['Outfit'] text-xl font-bold text-gray-900 mb-3">
                  Rapport d'expertise
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-4">
                  Vous avez un rapport d'expertise automobile ? Envoyez le PDF ou une photo 
                  du document. Notre système extraira les pièces à remplacer avec leurs 
                  prix estimés sur le marché de l'occasion.
                </p>
                <div className="flex items-center gap-2 text-[#8B5CF6] text-sm font-semibold">
                  <span>Analyser le rapport</span>
                  <ArrowRight size={16} />
                </div>
              </motion.button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const isDamageAnalysis = analysisType === 'damage';
  const isExpertise = analysisType === 'expertise';
  const accentColor = isDamageAnalysis ? '#E63946' : isExpertise ? '#8B5CF6' : '#2A9D8F';

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <Header />
      
      <main className="pt-24 pb-16 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <button
              onClick={() => { setAnalysisType(null); setImages([]); }}
              className="text-gray-400 hover:text-gray-900 text-sm mb-4 inline-flex items-center gap-2"
            >
              ← Changer de type d'analyse
            </button>
            <span className="block text-xs uppercase tracking-[0.3em] font-bold mb-4" style={{ color: accentColor }}>
              {isDamageAnalysis ? 'Analyse de Sinistre' : isExpertise ? 'Rapport d\'Expertise' : 'Recherche de Pièce'}
            </span>
            <h1 className="font-['Outfit'] text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {isDamageAnalysis ? 'Analysez les dommages de votre véhicule' : isExpertise ? 'Analysez votre rapport d\'expertise' : 'Identifiez votre pièce automobile'}
            </h1>
            <p className="text-gray-500 max-w-2xl mx-auto">
              {isDamageAnalysis 
                ? `Téléchargez jusqu'à 6 photos de votre véhicule accidenté. Notre système identifiera les zones endommagées et vous proposera les pièces concernées avec leurs prix.`
                : isExpertise
                ? `Téléchargez le PDF ou les photos de votre rapport d'expertise automobile. Notre système extraira toutes les pièces mentionnées avec leurs prix estimés.`
                : `Téléchargez jusqu'à 3 photos de la pièce que vous recherchez. Notre système l'identifiera et vous donnera son prix moyen sur le marché.`
              }
            </p>
          </motion.div>

          <AnimatePresence>
            {isAnalyzing && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-8"
              >
                <div className="bg-white border border-gray-200 p-8 shadow-sm">
                  <div className="flex items-center justify-center gap-4 mb-4">
                    <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: accentColor, borderTopColor: 'transparent' }}></div>
                    <span className="text-gray-900 font-semibold">Analyse en cours...</span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 overflow-hidden rounded-full">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${progress}%` }}
                      className="h-full rounded-full"
                      style={{ backgroundColor: accentColor }}
                    />
                  </div>
                  <p className="text-gray-500 text-sm text-center mt-4">
                    {isDamageAnalysis 
                      ? 'Notre système analyse vos photos pour détecter les zones endommagées...'
                      : isExpertise
                      ? 'Lecture et analyse du rapport d\'expertise en cours...'
                      : 'Identification de la pièce en cours...'
                    }
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
              >
                <h2 className="font-['Outfit'] text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Camera size={20} style={{ color: accentColor }} />
                  {isDamageAnalysis ? 'Photos du véhicule' : isExpertise ? 'Rapport d\'expertise (PDF ou photos)' : 'Photos de la pièce'}
                  <span className="text-gray-400 text-sm font-normal">({images.length}/{maxImages})</span>
                </h2>

                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed p-8 text-center cursor-pointer transition-all ${
                    isDragActive ? 'border-opacity-100 bg-opacity-5' : 'border-gray-300 hover:border-gray-400'
                  } ${images.length >= maxImages ? 'opacity-50 cursor-not-allowed' : ''}`}
                  style={{ 
                    borderColor: isDragActive ? accentColor : undefined,
                    backgroundColor: isDragActive ? `${accentColor}08` : 'white'
                  }}
                  data-testid="image-dropzone"
                >
                  <input {...getInputProps()} data-testid="image-input" />
                  <CloudArrowUp size={48} className="text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-700 font-medium mb-2">
                    {isDragActive ? 'Déposez vos photos ici' : 'Glissez-déposez vos photos'}
                  </p>
                  <p className="text-gray-400 text-sm">
                    {isExpertise 
                      ? 'ou cliquez pour sélectionner (PDF, JPG, PNG, WEBP - max 10 Mo)'
                      : 'ou cliquez pour sélectionner (JPG, PNG, WEBP - max 10 Mo)'
                    }
                  </p>
                </div>

                {/* Camera button for mobile */}
                {images.length < maxImages && (
                  <>
                    <input
                      ref={cameraInputRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handleCameraCapture}
                      className="hidden"
                      data-testid="camera-input"
                    />
                    <button
                      type="button"
                      onClick={() => cameraInputRef.current?.click()}
                      className="w-full mt-3 flex items-center justify-center gap-3 py-4 border-2 font-semibold text-sm transition-all"
                      style={{ borderColor: accentColor, color: accentColor }}
                      data-testid="camera-button"
                    >
                      <Camera size={22} weight="bold" />
                      {isExpertise ? 'Photographier le rapport' : 'Prendre une photo'}
                    </button>
                  </>
                )}

                {images.length > 0 && (
                  <div className="grid grid-cols-3 gap-3 mt-4">
                    {images.map((img, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="relative aspect-square bg-white border border-gray-200 overflow-hidden group shadow-sm"
                      >
                        <img
                          src={img.preview}
                          alt={`Preview ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-2 right-2 w-6 h-6 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          style={{ backgroundColor: accentColor }}
                          data-testid={`remove-image-${index}`}
                        >
                          <X size={14} weight="bold" />
                        </button>
                      </motion.div>
                    ))}
                    
                    {images.length < maxImages && (
                      <div
                        {...getRootProps()}
                        className="aspect-square bg-gray-50 border border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-gray-400 transition-colors"
                      >
                        <input {...getInputProps()} />
                        <ImageIcon size={24} className="text-gray-300" />
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-6 bg-white border border-gray-200 p-4 shadow-sm">
                  <h4 className="text-gray-900 font-semibold text-sm mb-2">
                    {isDamageAnalysis ? 'Conseils pour de meilleures photos :' : isExpertise ? 'Conseils pour votre rapport :' : 'Conseils pour identifier votre pièce :'}
                  </h4>
                  <ul className="text-gray-500 text-xs space-y-1">
                    {isDamageAnalysis ? (
                      <>
                        <li>• Prenez des photos en pleine lumière</li>
                        <li>• Capturez les zones endommagées de près</li>
                        <li>• Incluez une vue d'ensemble du véhicule</li>
                        <li>• Photographiez tous les angles impactés</li>
                      </>
                    ) : isExpertise ? (
                      <>
                        <li>• Envoyez le PDF complet du rapport d'expertise</li>
                        <li>• Si vous photographiez le document, assurez-vous qu'il soit lisible</li>
                        <li>• Incluez toutes les pages du rapport</li>
                        <li>• Les devis de réparation sont également acceptés</li>
                      </>
                    ) : (
                      <>
                        <li>• Photographiez la pièce de face et de profil</li>
                        <li>• Assurez-vous que la pièce est bien visible</li>
                        <li>• Incluez les références si visibles</li>
                        <li>• Évitez les photos floues ou trop sombres</li>
                      </>
                    )}
                  </ul>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                {isDamageAnalysis && (
                  <>
                    <div className="border border-gray-200 bg-white shadow-sm mb-6">
                      <button type="button" onClick={() => setShowOptionalFields(!showOptionalFields)} className="w-full px-5 py-4 flex items-center justify-between text-left" data-testid="toggle-optional-fields">
                        <div>
                          <h2 className="font-['Outfit'] text-base font-bold text-gray-700">
                            Informations v&eacute;hicule
                            <span className="ml-2 text-xs font-normal text-gray-400 uppercase tracking-wider">Optionnel</span>
                          </h2>
                          <p className="text-xs text-gray-400 mt-0.5">Pour des r&eacute;f&eacute;rences OEM plus pr&eacute;cises</p>
                        </div>
                        <CaretDown size={18} className={`text-gray-400 transition-transform ${showOptionalFields ? 'rotate-180' : ''}`} />
                      </button>
                      {showOptionalFields && (
                        <div className="px-5 pb-5 space-y-4 border-t border-gray-100 pt-4">
                          <div>
                            <label className="block text-gray-600 text-sm mb-2">Plaque d'immatriculation</label>
                            <input
                              type="text"
                              name="registration"
                              value={formData.registration}
                              onChange={handleInputChange}
                              placeholder="Ex: AB-123-CD"
                              className="w-full bg-white border border-gray-300 text-gray-900 p-3 focus:border-[#E63946] transition-colors font-mono uppercase"
                              data-testid="input-registration"
                            />
                          </div>
                          <div>
                            <label className="block text-gray-600 text-sm mb-2">Num&eacute;ro de chassis (VIN)</label>
                            <input
                              type="text"
                              name="vin"
                              value={formData.vin}
                              onChange={handleInputChange}
                              placeholder="Ex: VF1RFB00X56789012"
                              className="w-full bg-white border border-gray-300 text-gray-900 p-3 focus:border-[#E63946] transition-colors font-mono uppercase"
                              data-testid="input-vin"
                              maxLength={17}
                            />
                            <p className="text-gray-400 text-xs mt-1">17 caract&egrave;res - visible sur la carte grise ou le bas du pare-brise</p>
                          </div>
                          <div>
                            <label className="block text-gray-600 text-sm mb-2">Commentaire</label>
                            <textarea
                              name="comment"
                              value={formData.comment}
                              onChange={handleInputChange}
                              placeholder="D&eacute;crivez les circonstances de l'accident, les zones impact&eacute;es..."
                              rows={3}
                              className="w-full bg-white border border-gray-300 text-gray-900 p-3 focus:border-[#E63946] transition-colors resize-none"
                              data-testid="input-comment"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {isExpertise && (
                  <>
                    <div className="border border-gray-200 bg-white shadow-sm mb-6">
                      <button type="button" onClick={() => setShowOptionalFields(!showOptionalFields)} className="w-full px-5 py-4 flex items-center justify-between text-left" data-testid="toggle-optional-fields">
                        <div>
                          <h2 className="font-['Outfit'] text-base font-bold text-gray-700">
                            Informations compl&eacute;mentaires
                            <span className="ml-2 text-xs font-normal text-gray-400 uppercase tracking-wider">Optionnel</span>
                          </h2>
                          <p className="text-xs text-gray-400 mt-0.5">Pour des r&eacute;f&eacute;rences plus pr&eacute;cises</p>
                        </div>
                        <CaretDown size={18} className={`text-gray-400 transition-transform ${showOptionalFields ? 'rotate-180' : ''}`} />
                      </button>
                      {showOptionalFields && (
                        <div className="px-5 pb-5 space-y-4 border-t border-gray-100 pt-4">
                          <div>
                            <label className="block text-gray-600 text-sm mb-2">Plaque d'immatriculation</label>
                            <input type="text" name="registration" value={formData.registration} onChange={handleInputChange} placeholder="Ex: AB-123-CD" className="w-full bg-white border border-gray-300 text-gray-900 p-3 focus:border-[#8B5CF6] transition-colors font-mono uppercase" data-testid="input-registration" />
                          </div>
                          <div>
                            <label className="block text-gray-600 text-sm mb-2">Num&eacute;ro de chassis (VIN)</label>
                            <input type="text" name="vin" value={formData.vin} onChange={handleInputChange} placeholder="Ex: VF1RFB00X56789012" className="w-full bg-white border border-gray-300 text-gray-900 p-3 focus:border-[#8B5CF6] transition-colors font-mono uppercase" data-testid="input-vin" maxLength={17} />
                          </div>
                          <div>
                            <label className="block text-gray-600 text-sm mb-2">Commentaire</label>
                            <textarea name="comment" value={formData.comment} onChange={handleInputChange} placeholder="Pr&eacute;cisions sur le rapport, contexte du sinistre..." rows={3} className="w-full bg-white border border-gray-300 text-gray-900 p-3 focus:border-[#8B5CF6] transition-colors resize-none" data-testid="input-comment" />
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {!isDamageAnalysis && !isExpertise && (
                  <div className="border border-gray-200 bg-white shadow-sm mb-6">
                    <button type="button" onClick={() => setShowOptionalFields(!showOptionalFields)} className="w-full px-5 py-4 flex items-center justify-between text-left" data-testid="toggle-optional-fields">
                      <div>
                        <h2 className="font-['Outfit'] text-base font-bold text-gray-700">
                          Informations v&eacute;hicule
                          <span className="ml-2 text-xs font-normal text-gray-400 uppercase tracking-wider">Optionnel</span>
                        </h2>
                        <p className="text-xs text-gray-400 mt-0.5">Pour des r&eacute;f&eacute;rences OEM plus pr&eacute;cises</p>
                      </div>
                      <CaretDown size={18} className={`text-gray-400 transition-transform ${showOptionalFields ? 'rotate-180' : ''}`} />
                    </button>
                    {showOptionalFields && (
                      <div className="px-5 pb-5 space-y-4 border-t border-gray-100 pt-4">
                        <div>
                          <label className="block text-gray-600 text-sm mb-2">Plaque d'immatriculation</label>
                          <input type="text" name="registration" value={formData.registration} onChange={handleInputChange} placeholder="Ex: AB-123-CD" className="w-full bg-white border border-gray-300 text-gray-900 p-3 focus:border-[#2A9D8F] transition-colors font-mono uppercase" data-testid="input-registration" />
                        </div>
                        <div>
                          <label className="block text-gray-600 text-sm mb-2">Num&eacute;ro de chassis (VIN)</label>
                          <input type="text" name="vin" value={formData.vin} onChange={handleInputChange} placeholder="Ex: VF1RFB00X56789012" className="w-full bg-white border border-gray-300 text-gray-900 p-3 focus:border-[#2A9D8F] transition-colors font-mono uppercase" data-testid="input-vin" maxLength={17} />
                        </div>
                        <div>
                          <label className="block text-gray-600 text-sm mb-2">Commentaire</label>
                          <textarea name="comment" value={formData.comment} onChange={handleInputChange} placeholder="D&eacute;crivez la pi&egrave;ce que vous recherchez, le v&eacute;hicule d'origine..." rows={3} className="w-full bg-white border border-gray-300 text-gray-900 p-3 focus:border-[#2A9D8F] transition-colors resize-none" data-testid="input-comment" />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="bg-white border border-gray-200 p-4 mb-4 shadow-sm">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={gdprConsent}
                      onChange={(e) => setGdprConsent(e.target.checked)}
                      className="mt-1 w-5 h-5 rounded-none border-gray-300 bg-transparent"
                      data-testid="gdpr-checkbox"
                    />
                    <span className="text-gray-600 text-sm leading-relaxed">
                      J'accepte que mes photos soient analysées par le logiciel d'Ecocasse. 
                      Je comprends que cette analyse est purement indicative et ne constitue pas un diagnostic professionnel.
                    </span>
                  </label>
                </div>

                <div className="flex items-start gap-3 p-4 border mb-4 bg-amber-50 border-amber-200">
                  <Warning size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-600 text-xs leading-relaxed">
                    <strong className="text-gray-900">Important :</strong> Les prix affichés sont des estimations basées sur le marché de l'occasion. 
                    Le devis définitif sera établi après vérification.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || images.length === 0 || !gdprConsent}
                  className={`w-full py-5 text-sm font-bold uppercase tracking-wide flex items-center justify-center gap-3 transition-colors ${
                    isSubmitting || images.length === 0 || !gdprConsent
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'text-white hover:opacity-90'
                  }`}
                  style={{ backgroundColor: isSubmitting || images.length === 0 || !gdprConsent ? undefined : accentColor }}
                  data-testid="submit-analysis-btn"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Analyse en cours...
                    </>
                  ) : (
                    <>
                      <Check size={20} weight="bold" />
                      {isDamageAnalysis ? 'Analyser mon sinistre' : isExpertise ? 'Analyser le rapport' : 'Identifier ma pièce'}
                      <ArrowRight size={20} weight="bold" />
                    </>
                  )}
                </button>
              </motion.div>
            </div>
          </form>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AnalysisPage;
